/**
 * API Service - Axios instance and API functions
 */
import axios, { AxiosError, AxiosInstance } from "axios";
import type {
  Team,
  TeamMember,
  DocumentationLink,
  Repository,
  CompetencyMatrix,
  ResponsibilityMatrix,
  AIModelConfig,
  AvailableModel,
  OnboardingRequest,
  ApprovalAction,
  AuditLogEntry,
  AdminUser,
  ApprovalStats,
  BulkImportResult,
  AnalyticsDashboard,
  CommandStats,
  CommandPermission,
  AIContextTemplate,
  TeamExportData,
  TeamImportData,
  NewsletterTopic,
  NewsletterTopicListResponse,
  NewsletterCategoryStat,
  GitHubIssueListResponse,
  GitHubIssueStats,
  GitHubSyncResult,
  GitHubSyncLog,
  AIProviderConfig,
  AIProviderConfigUpdate,
  AIProviderTestResponse,
  AgentConfig,
  AgentConfigUpdate,
  AgentHealth,
  AgentSession,
  AgentTask,
  AgentUsage,
  UserGroupListResponse,
  UserGroupDetail,
  UserGroup,
  UserGroupCreate,
  UserGroupUpdate,
  TeamGroupLink,
  TeamGroupLinkResult,
  TeamGroupUnlinkResult,
  TeamGoogleOAuthConfig,
  TeamGoogleOAuthInitResponse,
  TeamGoogleOAuthTestResponse,
  CatalogEntry,
  TeamCatalogEntry,
  CatalogEntryCreate,
  InteractiveSessionItem,
} from "@/types";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api/v1";

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for auth tokens (if needed)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Handle unauthorized
      localStorage.removeItem("auth_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// =============================================================================
// TEAM API
// =============================================================================

export interface TeamListResponse {
  items: Team[];
  total: number;
  limit: number;
  offset: number;
}

export const teamApi = {
  list: (params?: {
    search?: string;
    approval_status?: string;
    is_active?: boolean;
    sort_by?: string;
    sort_order?: string;
    limit?: number;
    offset?: number;
    jira_project_key?: string;
    timezone?: string;
    description?: string;
    created_after?: string;
    created_before?: string;
    updated_after?: string;
    updated_before?: string;
  }) => api.get<TeamListResponse>("/teams", { params }),
  get: (id: string) => api.get<Team>(`/teams/${id}`),
  create: (data: Partial<Team>) => api.post<Team>("/teams", data),
  update: (id: string, data: Partial<Team>) =>
    api.put<Team>(`/teams/${id}`, data),
  delete: (id: string) => api.delete(`/teams/${id}`),

  // Members
  getMembers: (teamId: string) =>
    api.get<TeamMember[]>(`/teams/${teamId}/members`),
  addMember: (teamId: string, data: Partial<TeamMember>) =>
    api.post<TeamMember>(`/teams/${teamId}/members`, data),
  updateMember: (teamId: string, memberId: string, data: Partial<TeamMember>) =>
    api.put<TeamMember>(`/teams/${teamId}/members/${memberId}/role`, data),
  removeMember: (teamId: string, memberId: string) =>
    api.delete(`/teams/${teamId}/members/${memberId}`),
  importMembers: (teamId: string, members: Partial<TeamMember>[]) =>
    api.post<BulkImportResult>(`/teams/${teamId}/import/members`, { members }),

  // Group links
  getLinkedGroups: (teamId: string) =>
    api.get<TeamGroupLink[]>(`/teams/${teamId}/linked-groups`),
  linkGroup: (teamId: string, groupId: string) =>
    api.post<TeamGroupLinkResult>(`/teams/${teamId}/linked-groups`, { group_id: groupId }),
  unlinkGroup: (teamId: string, groupId: string) =>
    api.delete<TeamGroupUnlinkResult>(`/teams/${teamId}/linked-groups/${groupId}`),

  // Documentation
  getDocumentation: (teamId: string) =>
    api.get<DocumentationLink[]>(`/teams/${teamId}/documentation`),
  addDocumentation: (teamId: string, data: Partial<DocumentationLink>) =>
    api.post<DocumentationLink>(`/teams/${teamId}/documentation`, data),
  importDocumentation: (teamId: string, items: Partial<DocumentationLink>[]) =>
    api.post<BulkImportResult>(`/teams/${teamId}/import/documentation`, {
      items,
    }),
  updateDocumentation: (teamId: string, docId: string, data: Partial<DocumentationLink>) =>
    api.put<DocumentationLink>(`/teams/${teamId}/documentation/${docId}`, data),
  removeDocumentation: (teamId: string, docId: string) =>
    api.delete(`/teams/${teamId}/documentation/${docId}`),

  // Repositories
  getRepositories: (teamId: string) =>
    api.get<Repository[]>(`/teams/${teamId}/repositories`),
  addRepository: (teamId: string, data: Partial<Repository>) =>
    api.post<Repository>(`/teams/${teamId}/repositories`, data),
  importRepositories: (teamId: string, items: Partial<Repository>[]) =>
    api.post<BulkImportResult>(`/teams/${teamId}/import/repositories`, {
      items,
    }),
  updateRepository: (teamId: string, repoId: string, data: Partial<Repository>) =>
    api.put<Repository>(`/teams/${teamId}/repositories/${repoId}`, data),
  removeRepository: (teamId: string, repoId: string) =>
    api.delete(`/teams/${teamId}/repositories/${repoId}`),

  // Experts (Competency Matrix)
  getExperts: (teamId: string) =>
    api.get<CompetencyMatrix[]>(`/teams/${teamId}/experts`),
  addExpert: (teamId: string, data: Partial<CompetencyMatrix>) =>
    api.post<CompetencyMatrix>(`/teams/${teamId}/experts`, data),
  updateExpert: (
    teamId: string,
    expertId: string,
    data: Partial<CompetencyMatrix>,
  ) =>
    api.put<CompetencyMatrix>(`/teams/${teamId}/experts/${expertId}`, data),
  removeExpert: (teamId: string, expertId: string) =>
    api.delete(`/teams/${teamId}/experts/${expertId}`),
  importExperts: (teamId: string, experts: Partial<CompetencyMatrix>[]) =>
    api.post<BulkImportResult>(`/teams/${teamId}/import/experts`, { experts }),

  // AI Model Config
  getAIConfig: (teamId: string) =>
    api.get<AIModelConfig>(`/teams/${teamId}/ai-config`),
  updateAIConfig: (teamId: string, data: Partial<AIModelConfig>) =>
    api.put<AIModelConfig>(`/teams/${teamId}/ai-config`, data),
  getAvailableModels: () => api.get<AvailableModel[]>("/ai/models"),

  // AI Provider Config
  getAIProviderConfig: (teamId: string) =>
    api.get<AIProviderConfig>(`/teams/${teamId}/ai-provider-config`),
  updateAIProviderConfig: (teamId: string, data: AIProviderConfigUpdate) =>
    api.put<{ message: string }>(`/teams/${teamId}/ai-provider-config`, data),
  clearAIProviderConfig: (teamId: string) =>
    api.delete<{ message: string }>(`/teams/${teamId}/ai-provider-config`),
  testAIProviderConfig: (teamId: string, data: AIProviderConfigUpdate) =>
    api.post<AIProviderTestResponse>(
      `/teams/${teamId}/ai-provider-config/test`,
      data,
    ),

  // Responsibility Matrix
  getResponsibilities: (teamId: string) =>
    api.get<ResponsibilityMatrix[]>(`/teams/${teamId}/responsibilities`),
  addResponsibility: (teamId: string, data: Partial<ResponsibilityMatrix>) =>
    api.post<ResponsibilityMatrix>(`/teams/${teamId}/responsibilities`, data),
  updateResponsibility: (
    teamId: string,
    respId: string,
    data: Partial<ResponsibilityMatrix>,
  ) =>
    api.put<ResponsibilityMatrix>(
      `/teams/${teamId}/responsibilities/${respId}`,
      data,
    ),
  removeResponsibility: (teamId: string, respId: string) =>
    api.delete(`/teams/${teamId}/responsibilities/${respId}`),
  importResponsibilities: (
    teamId: string,
    items: Partial<ResponsibilityMatrix>[],
  ) =>
    api.post<BulkImportResult>(`/teams/${teamId}/import/responsibilities`, {
      items,
    }),

  // Command Permissions
  getCommandPermissions: (teamId: string) =>
    api.get<CommandPermission[]>(`/teams/${teamId}/command-permissions`),
  addCommandPermission: (teamId: string, data: Partial<CommandPermission>) =>
    api.post<CommandPermission>(`/teams/${teamId}/command-permissions`, data),
  updateCommandPermission: (
    teamId: string,
    permId: string,
    data: Partial<CommandPermission>,
  ) =>
    api.put<CommandPermission>(
      `/teams/${teamId}/command-permissions/${permId}`,
      data,
    ),
  removeCommandPermission: (teamId: string, permId: string) =>
    api.delete(`/teams/${teamId}/command-permissions/${permId}`),

  // AI Context Templates
  getContextTemplates: (teamId: string) =>
    api.get<AIContextTemplate[]>(`/teams/${teamId}/context-templates`),
  addContextTemplate: (teamId: string, data: Partial<AIContextTemplate>) =>
    api.post<AIContextTemplate>(`/teams/${teamId}/context-templates`, data),
  updateContextTemplate: (
    teamId: string,
    templateId: string,
    data: Partial<AIContextTemplate>,
  ) =>
    api.put<AIContextTemplate>(
      `/teams/${teamId}/context-templates/${templateId}`,
      data,
    ),
  removeContextTemplate: (teamId: string, templateId: string) =>
    api.delete(`/teams/${teamId}/context-templates/${templateId}`),

  // Google OAuth per-team
  getGoogleOAuthConfig: (teamId: string) =>
    api.get<TeamGoogleOAuthConfig>(`/teams/${teamId}/google-oauth-config`),
  initGoogleOAuth: (teamId: string) =>
    api.post<TeamGoogleOAuthInitResponse>(`/teams/${teamId}/google-oauth-config/init`),
  clearGoogleOAuth: (teamId: string) =>
    api.delete<{ message: string }>(`/teams/${teamId}/google-oauth-config`),
  testGoogleOAuth: (teamId: string) =>
    api.get<TeamGoogleOAuthTestResponse>(`/teams/${teamId}/google-oauth-config/test`),

  // Team Export/Import
  exportTeam: (teamId: string) =>
    api.get<TeamExportData>(`/teams/${teamId}/export`),
  importTeam: (teamId: string, data: TeamImportData) =>
    api.post<BulkImportResult>(`/teams/${teamId}/import`, data),

  // Prompt Configuration
  getPromptConfig: (teamId: string) =>
    api.get<PromptConfig>(`/teams/${teamId}/prompt-config`),
  updatePromptConfig: (teamId: string, data: Partial<PromptConfig>) =>
    api.put<PromptConfig>(`/teams/${teamId}/prompt-config`, data),

  // Jira Team View
  getJiraViewEpics: (teamId: string, page: number = 1) =>
    api.get(`/teams/${teamId}/jira-view/epics`, { params: { page } }),
  getJiraViewActive: (teamId: string) =>
    api.get(`/teams/${teamId}/jira-view/active`),
  getJiraViewBacklog: (teamId: string) =>
    api.get(`/teams/${teamId}/jira-view/backlog`),
};

// JIRA ticket template configuration
export interface JiraTemplateConfig {
  template: string;
  placeholders: Record<string, string>;
}

// JIRA templates per issue type
export type JiraTicketTemplates = Record<string, JiraTemplateConfig>;

// PromptConfig type
export interface PromptConfig {
  id: string;
  team_id: string;
  team_display_name: string;
  bot_persona: string;
  team_lead_name: string | null;
  team_lead_email: string | null;
  team_lead_tag: string | null;
  team_lead_google_chat_user_id: string | null; // For clickable @mentions (e.g., users/123456789)
  system_prompt: string;
  out_of_scope_response: string;
  notice_text: string;
  response_override: string | null;
  prioritization_keywords: string[];
  enable_expert_tagging: boolean;
  enable_prioritization_escalation: boolean;
  enable_multiple_thread_responses: boolean;
  enable_auto_replies: boolean; // When disabled, bot replies only to @mentions and ! commands
  enable_response_caching: boolean;
  disable_auto_expert_append: boolean;
  enable_web_fetch: boolean; // When enabled, bot can fetch content from URLs in messages
  enable_proactive_docs_fetch: boolean; // When enabled, bot fetches live content from docs/repos
  enable_jira_commands: boolean; // When enabled, users can use !jira commands
  enable_cache_review_context: boolean; // When enabled, include Cache Review entries in AI context
  enable_github_issues: boolean; // When enabled, show GitHub Issues tracking dashboard
  enable_agent: boolean; // When enabled, show Agent tab and accept !agent commands
  enable_jira_spy: boolean; // When enabled, agent auto-picks Jira tickets with configured label
  enable_jira_ai_execution: boolean; // When enabled, agent runs tickets hands-off through implementation (requires spec-driven mode)
  enable_jira_team_view: boolean; // When enabled, show Team View tab with epic priorities, active kanban by assignee, and backlog
  jira_ticket_templates: JiraTicketTemplates | null; // Custom JIRA templates per issue type
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// ADMIN / APPROVAL API
// =============================================================================

export const adminApi = {
  // Onboarding requests
  createRequest: (data: {
    team_id: string;
    requested_by_email: string;
    notes?: string;
  }) => api.post<OnboardingRequest>("/admin/onboarding/request", data),
  getRequest: (requestId: string) =>
    api.get<OnboardingRequest>(`/admin/onboarding/request/${requestId}`),
  getTeamStatus: (teamId: string) =>
    api.get<OnboardingRequest>(`/admin/onboarding/team/${teamId}`),
  resubmit: (
    requestId: string,
    data: { requested_by_email: string; notes?: string },
  ) =>
    api.post<OnboardingRequest>(
      `/admin/onboarding/request/${requestId}/resubmit`,
      data,
    ),

  // Admin actions
  listPending: (params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<{
      total: number;
      pending: number;
      approved: number;
      rejected: number;
      revision_requested: number;
      requests: OnboardingRequest[];
    }>("/admin/onboarding/pending", { params }),
  approve: (requestId: string, action: ApprovalAction) =>
    api.post<OnboardingRequest>(
      `/admin/onboarding/request/${requestId}/approve`,
      action,
    ),
  reject: (requestId: string, action: ApprovalAction) =>
    api.post<OnboardingRequest>(
      `/admin/onboarding/request/${requestId}/reject`,
      action,
    ),
  requestRevision: (requestId: string, action: ApprovalAction) =>
    api.post<OnboardingRequest>(
      `/admin/onboarding/request/${requestId}/request-revision`,
      action,
    ),

  // Audit log
  getAuditLog: (requestId: string) =>
    api.get<AuditLogEntry[]>(`/admin/onboarding/request/${requestId}/audit`),

  // Admin users
  createAdmin: (data: Partial<AdminUser>) =>
    api.post<AdminUser>("/admin/admins", data),
  listAdmins: (includeInactive?: boolean) =>
    api.get<AdminUser[]>("/admin/admins", {
      params: { include_inactive: includeInactive },
    }),
  getAdmin: (adminId: string) => api.get<AdminUser>(`/admin/admins/${adminId}`),
  updateAdmin: (adminId: string, data: Partial<AdminUser>) =>
    api.patch<AdminUser>(`/admin/admins/${adminId}`, data),

  // Statistics
  getStats: () => api.get<ApprovalStats>("/admin/stats"),

  // User management
  listUsers: (params?: {
    search?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }) => api.get<UserListResponse>("/admin/users", { params }),
  getUser: (userId: string) => api.get<UserInfo>(`/admin/users/${userId}`),
  updateUser: (
    userId: string,
    data: { display_name?: string; is_active?: boolean },
  ) => api.patch<UserInfo>(`/admin/users/${userId}`, data),
  updateUserAdmin: (
    userId: string,
    data: { is_admin: boolean; is_super_admin?: boolean },
  ) => api.patch<UserInfo>(`/admin/users/${userId}/admin`, data),
  deleteUser: (userId: string) => api.delete(`/admin/users/${userId}`),
  resetUserPassword: (userId: string, newPassword: string) =>
    api.patch<UserInfo>(`/admin/users/${userId}/password`, {
      new_password: newPassword,
    }),

  // Team management
  listTeamsAdmin: (params?: {
    search?: string;
    approval_status?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }) => api.get<TeamAdminListResponse>("/admin/teams", { params }),
  updateTeamAdmin: (
    teamId: string,
    data: { is_active?: boolean; approval_status?: string },
  ) => api.patch<TeamAdminInfo>(`/admin/teams/${teamId}`, data),
  deleteTeamAdmin: (teamId: string) => api.delete(`/admin/teams/${teamId}`),
  permanentlyDeleteTeamAdmin: (teamId: string) =>
    api.delete(`/admin/teams/${teamId}/permanent`),

  // Invitation codes
  listInvitationCodes: (params?: {
    is_used?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<InvitationCodeListResponse>("/admin/invitation-codes", { params }),
  generateInvitationCode: (data?: {
    email_hint?: string;
    expires_in_days?: number;
  }) => api.post<InvitationCodeResponse>("/admin/invitation-codes", data || {}),
  deleteInvitationCode: (codeId: string) =>
    api.delete(`/admin/invitation-codes/${codeId}`),

  // User groups
  listGroups: (params?: { search?: string; limit?: number; offset?: number }) =>
    api.get<UserGroupListResponse>("/admin/groups", { params }),
  getGroup: (groupId: string) =>
    api.get<UserGroupDetail>(`/admin/groups/${groupId}`),
  createGroup: (data: UserGroupCreate) =>
    api.post<UserGroup>("/admin/groups", data),
  updateGroup: (groupId: string, data: UserGroupUpdate) =>
    api.put<UserGroup>(`/admin/groups/${groupId}`, data),
  deleteGroup: (groupId: string) =>
    api.delete(`/admin/groups/${groupId}`),
  addGroupMember: (groupId: string, userId: string) =>
    api.post<{ added: number; already_in_group: number }>(
      `/admin/groups/${groupId}/members`,
      { user_id: userId },
    ),
  removeGroupMember: (groupId: string, userId: string) =>
    api.delete(`/admin/groups/${groupId}/members/${userId}`),
};

// User types
export interface UserInfo {
  id: string;
  email: string;
  display_name: string | null;
  is_active: boolean;
  is_verified: boolean;
  is_admin: boolean;
  is_super_admin: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserListResponse {
  items: UserInfo[];
  total: number;
  limit: number;
  offset: number;
}

// Team admin types
export interface TeamAdminInfo {
  id: string;
  name: string;
  description: string | null;
  google_space_id: string;
  jira_project_key: string | null;
  is_active: boolean;
  approval_status: string;
  approved_by: string | null;
  approved_at: string | null;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface TeamAdminListResponse {
  items: TeamAdminInfo[];
  total: number;
  limit: number;
  offset: number;
}

// Invitation code types
export interface InvitationCodeResponse {
  id: string;
  code: string;
  created_by_email: string | null;
  used_by_email: string | null;
  email_hint: string | null;
  is_used: boolean;
  expires_at: string | null;
  created_at: string;
  used_at: string | null;
}

export interface InvitationCodeListResponse {
  items: InvitationCodeResponse[];
  total: number;
  limit: number;
  offset: number;
}

// =============================================================================
// ANALYTICS API
// =============================================================================

export const analyticsApi = {
  getDashboard: (teamId: string, days?: number) =>
    api.get<AnalyticsDashboard>(`/analytics/teams/${teamId}/dashboard`, {
      params: { days },
    }),
  getCommandStats: (teamId: string) =>
    api.get<CommandStats[]>(`/analytics/teams/${teamId}/commands/stats`),
  exportIssues: (teamId: string, format: "csv" | "json") =>
    api.get(`/analytics/teams/${teamId}/export`, {
      params: { format },
      responseType: format === "csv" ? "blob" : "json",
    }),
};

// =============================================================================
// SAMPLE FILES API
// =============================================================================

export const sampleFilesApi = {
  download: (type: string, format: "json" | "csv") =>
    api.get(`/teams/samples/${type}`, {
      params: { format },
      responseType: "blob",
    }),
};

// =============================================================================
// ACTIVITY API
// =============================================================================

export interface Activity {
  id: string;
  team_id: string | null;
  activity_type: string;
  title: string;
  description: string | null;
  actor_email: string | null;
  actor_name: string | null;
  extra_data: Record<string, unknown> | null;
  is_system: boolean;
  created_at: string;
}

export interface ActivityListResponse {
  items: Activity[];
  total: number;
  limit: number;
  offset: number;
}

export const activityApi = {
  list: (params?: {
    team_id?: string;
    activity_type?: string;
    include_system?: boolean;
    limit?: number;
    offset?: number;
  }) => api.get<ActivityListResponse>("/activity", { params }),

  getRecent: (params?: { hours?: number; limit?: number }) =>
    api.get<Activity[]>("/activity/recent", { params }),

  getSummary: (days?: number) =>
    api.get<Record<string, number>>("/activity/summary", { params: { days } }),

  getTeamActivities: (
    teamId: string,
    params?: { limit?: number; offset?: number },
  ) => api.get<ActivityListResponse>(`/activity/team/${teamId}`, { params }),

  getAll: (params?: {
    activity_type?: string;
    limit?: number;
    offset?: number;
  }) => api.get<ActivityListResponse>("/activity/all", { params }),
};

// =============================================================================
// AUTH API
// =============================================================================

export interface CheckEmailResponse {
  exists: boolean;
  email: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: {
    email: string;
    displayName: string;
    role: string;
    is_admin: boolean;
    team_ids: string[];
    all_team_ids: string[];
  };
}

export const authApi = {
  checkEmail: (email: string) =>
    api.post<CheckEmailResponse>("/auth/check-email", { email }),
  register: (
    email: string,
    password: string,
    invitation_code: string,
    display_name?: string,
  ) =>
    api.post<TokenResponse>("/auth/register", {
      email,
      password,
      invitation_code,
      display_name,
    }),
  login: (email: string, password: string) =>
    api.post<TokenResponse>("/auth/login", { email, password }),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
  getGitHubAuthUrl: () =>
    api.get<{ authorization_url: string }>("/auth/github/init"),
  exchangeCode: (code: string) =>
    api.post<TokenResponse>("/auth/exchange", { code }),
};

// =============================================================================
// INTEGRATIONS API
// =============================================================================

export interface GoogleOAuthInitResponse {
  authorization_url: string;
  state: string;
  message: string;
}

export interface GoogleOAuthStatusResponse {
  configured: boolean;
  valid: boolean | null;
  error: string | null;
  action_required: string | null;
}

export interface ConfluenceSSOStatusResponse {
  configured: boolean;
  valid: boolean | null;
  auth_method: string | null;
  cookie_names: string[];
  base_url: string | null;
  error: string | null;
  action_required: string | null;
}

export interface ConfluenceSSOCookiesResponse {
  success: boolean;
  message: string;
  cookie_names: string[];
}

export interface IntegrationStatus {
  name: string;
  status: "connected" | "disconnected" | "error";
  message: string | null;
}

export interface PollingStatus {
  enabled: boolean;
  running: boolean;
  next_poll: string | null;
}

export interface HealthDetailedResponse {
  status: string;
  version: string;
  environment: string;
  dependencies: Record<string, string>;
  polling: PollingStatus;
}

export interface AIOrbitModelsResponse {
  models: string[];
  default: string;
  fallback: string;
  source: "live" | "persisted" | "static";
}

export const integrationsApi = {
  // AIOrbit chat model list (live, cached server-side; last synced copy; static fallback)
  getAIOrbitModels: () =>
    api.get<AIOrbitModelsResponse>("/integrations/aiorbit/models"),

  // Google OAuth
  googleAuthInit: (redirectUri?: string) =>
    api.get<GoogleOAuthInitResponse>("/integrations/google/auth/init", {
      params: redirectUri ? { redirect_uri: redirectUri } : undefined,
    }),
  googleAuthStatus: () =>
    api.get<GoogleOAuthStatusResponse>("/integrations/google/auth/status"),

  // Confluence SSO
  confluenceAuthStatus: () =>
    api.get<ConfluenceSSOStatusResponse>(
      "/integrations/confluence/auth/status",
    ),
  confluenceSaveCookies: (cookies: Record<string, string>) =>
    api.post<ConfluenceSSOCookiesResponse>(
      "/integrations/confluence/auth/cookies",
      { cookies },
    ),
  confluenceClearCookies: () =>
    api.delete("/integrations/confluence/auth/cookies"),
  confluenceTestConnection: () => api.get("/integrations/confluence/test"),

  // Integration status
  getStatus: () => api.get<IntegrationStatus[]>("/integrations/status"),

  // Health (includes polling status)
  getDetailedHealth: () => api.get<HealthDetailedResponse>("/health/detailed"),
};

// =============================================================================
// FEEDBACK API
// =============================================================================

export interface Feedback {
  id: string;
  team_id: string | null;
  submitted_by_email: string;
  submitted_by_name: string | null;
  feedback_type:
    | "bug_report"
    | "feature_request"
    | "improvement"
    | "question"
    | "other";
  title: string;
  description: string;
  priority: "low" | "medium" | "high" | "critical";
  status:
    | "new"
    | "under_review"
    | "planned"
    | "in_progress"
    | "resolved"
    | "declined";
  admin_response: string | null;
  responded_by: string | null;
  responded_at: string | null;
  vote_count: number;
  created_at: string;
  updated_at: string;
}

export interface FeedbackListResponse {
  items: Feedback[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface FeedbackStats {
  total: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  by_priority: Record<string, number>;
}

export interface FeedbackCreate {
  team_id?: string;
  feedback_type?: string;
  title: string;
  description: string;
  priority?: string;
  browser_info?: string;
  page_url?: string;
}

export interface FeedbackAdminUpdate {
  status?: string;
  admin_response?: string;
  priority?: string;
}

export const feedbackApi = {
  // Create feedback
  create: (data: FeedbackCreate, userEmail: string, userName?: string) =>
    api.post<Feedback>("/feedback", data, {
      params: { submitted_by_email: userEmail, submitted_by_name: userName },
    }),

  // List feedback
  list: (params?: {
    page?: number;
    page_size?: number;
    status?: string;
    type?: string;
    team_id?: string;
    submitted_by?: string;
  }) => api.get<FeedbackListResponse>("/feedback", { params }),

  // Get stats
  getStats: (teamId?: string) =>
    api.get<FeedbackStats>("/feedback/stats", { params: { team_id: teamId } }),

  // Get single feedback
  get: (feedbackId: string) => api.get<Feedback>(`/feedback/${feedbackId}`),

  // Update feedback (submitter)
  update: (feedbackId: string, data: Partial<FeedbackCreate>) =>
    api.put<Feedback>(`/feedback/${feedbackId}`, data),

  // Admin update
  adminUpdate: (
    feedbackId: string,
    data: FeedbackAdminUpdate,
    adminEmail: string,
  ) =>
    api.put<Feedback>(`/feedback/${feedbackId}/admin`, data, {
      params: { admin_email: adminEmail },
    }),

  // Vote
  vote: (feedbackId: string) =>
    api.post<Feedback>(`/feedback/${feedbackId}/vote`),

  // Delete
  delete: (feedbackId: string) => api.delete(`/feedback/${feedbackId}`),
};

// =============================================================================
// AI TODOS API
// =============================================================================

export interface AiTodo {
  id: string;
  team_id: string;
  title: string;
  description: string | null;
  sample_queries: string[] | null;
  sample_responses: string[] | null;
  occurrence_count: number;
  source_month: string;
  cache_review_ids: string[] | null;
  status: "pending" | "in_progress" | "completed" | "dismissed";
  priority: "low" | "medium" | "high";
  jira_ticket_key: string | null;
  jira_ticket_url: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface AiTodoListResponse {
  items: AiTodo[];
  total: number;
  page: number;
  page_size: number;
}

export interface AiTodoStats {
  total: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
}

export interface AnalyzeRequest {
  team_id: string;
  year: number;
  month: number;
  min_occurrences?: number;
  similarity_threshold?: number;
}

export const aiTodosApi = {
  // List todos with filtering
  list: (params?: {
    team_id?: string;
    status?: string;
    priority?: string;
    page?: number;
    page_size?: number;
  }) => api.get<AiTodoListResponse>("/todos", { params }),

  // Get stats
  getStats: (teamId?: string) =>
    api.get<AiTodoStats>("/todos/stats", { params: { team_id: teamId } }),

  // Get single todo
  get: (todoId: string) => api.get<AiTodo>(`/todos/${todoId}`),

  // Trigger monthly analysis
  analyze: (data: AnalyzeRequest) => api.post<AiTodo[]>("/todos/analyze", data),

  // Update todo
  update: (
    todoId: string,
    data: Partial<
      Pick<AiTodo, "status" | "priority" | "title" | "description">
    >,
  ) => api.patch<AiTodo>(`/todos/${todoId}`, data),

  // Mark as complete
  complete: (todoId: string) => api.post<AiTodo>(`/todos/${todoId}/complete`),

  // Create JIRA ticket
  createJira: (todoId: string, issueType?: string) =>
    api.post<AiTodo>(`/todos/${todoId}/create-jira`, {
      issue_type: issueType || "Task",
    }),

  // Dismiss/delete
  dismiss: (todoId: string) => api.delete(`/todos/${todoId}`),

  // Get count of unanalyzed CacheReview entries
  getUnanalyzedCount: (teamId: string, year?: number, month?: number) =>
    api.get<{ unanalyzed_count: number; team_id: string }>(
      "/todos/unanalyzed-count",
      {
        params: { team_id: teamId, year, month },
      },
    ),
};

// =============================================================================
// CACHE REVIEW API (All Interactions)
// =============================================================================

export interface CacheReviewEntry {
  id: string;
  team_id: string;
  thread_id: string | null;
  user_query: string;
  bot_response: string;
  context_type: string;
  external_context: string | null;
  user_email: string | null;
  processing_time_ms: number | null;
  was_cache_hit: boolean;
  review_status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  entry_date: string;
  created_at: string;
}

export interface CacheReviewListResponse {
  items: CacheReviewEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface CacheReviewStats {
  total: number;
  by_context_type: Record<string, number>;
  by_review_status: Record<string, number>;
  cache_hits: number;
  cache_misses: number;
}

export const cacheReviewApi = {
  // List all interactions
  list: (
    teamId: string,
    params?: {
      context_type?: string;
      review_status?: string;
      page?: number;
      page_size?: number;
    },
  ) =>
    api.get<CacheReviewListResponse>(`/todos/cache-review/${teamId}`, {
      params,
    }),

  // Get stats
  getStats: (teamId: string) =>
    api.get<CacheReviewStats>(`/todos/cache-review/${teamId}/stats`),
};

// Newsletter Topics API
export const newsletterApi = {
  list: (params?: {
    from_date?: string;
    to_date?: string;
    category?: string;
    author?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) => api.get<NewsletterTopicListResponse>('/admin/newsletter/topics', { params }),

  get: (topicId: string) => api.get<NewsletterTopic>(`/admin/newsletter/topics/${topicId}`),

  delete: (topicId: string) => api.delete(`/admin/newsletter/topics/${topicId}`),

  exportMarkdown: (fromDate: string, toDate: string, category?: string) =>
    api.get('/admin/newsletter/export', {
      params: { from_date: fromDate, to_date: toDate, category },
      responseType: 'blob',
    }),

  getCategoryStats: () => api.get<NewsletterCategoryStat[]>('/admin/newsletter/categories'),

  // Newsletter settings
  getSettings: () =>
    api.get<{
      newsletter_space_id: string | null;
      enable_newsletter_polling: boolean;
      ai_provider_type: string | null;
      ai_api_endpoint: string | null;
      has_api_key: boolean;
      ai_model_override: string | null;
    }>('/admin/settings/newsletter'),

  updateSettings: (settings: {
    newsletter_space_id?: string;
    enable_newsletter_polling?: boolean;
    ai_provider_type?: string | null;
    ai_api_endpoint?: string | null;
    ai_api_key?: string | null;
    ai_model_override?: string | null;
  }) => api.post('/admin/settings/newsletter', settings),

  testAIConnection: (config: {
    provider_type?: string | null;
    api_endpoint?: string | null;
    api_key?: string | null;
    model_override?: string | null;
  }) => api.post<{ status: string; message: string }>('/admin/settings/newsletter/test-ai', config),
};

// =============================================================================
// LOGS API (admin only)
// =============================================================================

export interface LogEntry {
  id: number;
  timestamp: string;
  level: string;
  logger: string;
  message: string;
  module: string;
  funcName: string;
  lineno: number;
  exception?: string;
}

export interface LogStats {
  total_entries: number;
  max_entries: number;
  level_counts: Record<string, number>;
  oldest_id: number | null;
  newest_id: number | null;
}

export interface LogsResponse {
  entries: LogEntry[];
  stats: LogStats;
}

export interface LogHistogramBucket {
  hour: string;
  total: number;
  debug: number;
  info: number;
  warning: number;
  error: number;
  critical: number;
  first_id: number | null;
  last_id: number | null;
}

export const logsApi = {
  getLogs: (params?: {
    limit?: number;
    level?: string;
    logger_name?: string;
    search?: string;
    after_id?: number;
  }) => api.get<LogsResponse>('/admin/logs', { params }),

  getStats: () => api.get<LogStats>('/admin/logs/stats'),

  getHistogram: (hours?: number) =>
    api.get<LogHistogramBucket[]>('/admin/logs/histogram', {
      params: { hours: hours || 48 },
    }),
};

// =============================================================================
// GITHUB ISSUES API
// =============================================================================

export const githubIssuesApi = {
  list: (
    teamId: string,
    params?: {
      repository_id?: string;
      state?: string;
      has_jira?: boolean;
      page?: number;
      page_size?: number;
    },
  ) =>
    api.get<GitHubIssueListResponse>(`/teams/${teamId}/github-issues`, {
      params,
    }),

  getStats: (teamId: string) =>
    api.get<GitHubIssueStats>(`/teams/${teamId}/github-issues/stats`),

  syncAll: (teamId: string) =>
    api.post<GitHubSyncResult[]>(`/teams/${teamId}/github-issues/sync`),

  syncRepo: (teamId: string, repoId: string) =>
    api.post<GitHubSyncResult>(
      `/teams/${teamId}/github-issues/sync/${repoId}`,
    ),

  getSyncLogs: (teamId: string, limit?: number) =>
    api.get<GitHubSyncLog[]>(`/teams/${teamId}/github-issues/sync-logs`, {
      params: { limit },
    }),

  createJira: (teamId: string, issueId: string, issueType?: string) =>
    api.post(`/teams/${teamId}/github-issues/${issueId}/create-jira`, {
      issue_type: issueType || "Task",
    }),

  createJiraBulk: (
    teamId: string,
    issueIds: string[],
    issueType?: string,
  ) =>
    api.post(`/teams/${teamId}/github-issues/create-jira-bulk`, {
      issue_ids: issueIds,
      issue_type: issueType || "Task",
    }),

  updateAutoJira: (
    teamId: string,
    repoId: string,
    autoCreateJira: boolean,
  ) =>
    api.put(`/teams/${teamId}/repositories/${repoId}/auto-jira`, {
      auto_create_jira: autoCreateJira,
    }),

  updateAutoJiraBulk: (
    teamId: string,
    updates: Array<{ repository_id: string; auto_create_jira: boolean }>,
  ) => api.put(`/teams/${teamId}/repositories/bulk-auto-jira`, { updates }),

  linkJira: (teamId: string, issueId: string, jiraTicketKey: string) =>
    api.put(`/teams/${teamId}/github-issues/${issueId}/link-jira`, {
      jira_ticket_key: jiraTicketKey,
    }),

  unlinkJira: (teamId: string, issueId: string) =>
    api.delete(`/teams/${teamId}/github-issues/${issueId}/unlink-jira`),
};

// =============================================================================
// AGENT API
// =============================================================================

export interface InteractiveSession {
  session_id: string;
  console_token: string;
  idle_timeout_hours: number;
}

export const agentApi = {
  getConfig: (teamId: string) =>
    api.get<AgentConfig>(`/teams/${teamId}/agent/config`),
  updateConfig: (teamId: string, data: AgentConfigUpdate) =>
    api.put<{ message: string }>(`/teams/${teamId}/agent/config`, data),

  getTasks: (teamId: string, status?: string) =>
    api.get<AgentTask[]>(`/teams/${teamId}/agent/tasks`, {
      params: status ? { status } : undefined,
    }),
  getTask: (teamId: string, taskId: string) =>
    api.get<AgentTask>(`/teams/${teamId}/agent/tasks/${taskId}`),
  cancelTask: (teamId: string, taskId: string) =>
    api.delete<{ message: string }>(`/teams/${teamId}/agent/tasks/${taskId}`),
  retryTask: (teamId: string, taskId: string) =>
    api.post<{ message: string }>(`/teams/${teamId}/agent/tasks/${taskId}/retry`),
  deleteTask: (teamId: string, taskId: string) =>
    api.delete<{ message: string }>(`/teams/${teamId}/agent/tasks/${taskId}/permanent`),

  getSessions: (teamId: string) =>
    api.get<AgentSession[]>(`/teams/${teamId}/agent/sessions`),

  getUsage: (teamId: string) =>
    api.get<AgentUsage>(`/teams/${teamId}/agent/usage`),

  getHealth: (teamId: string) =>
    api.get<AgentHealth>(`/teams/${teamId}/agent/health`),

  restartRunner: (teamId: string) =>
    api.post<{ message: string }>(`/teams/${teamId}/agent/restart`),

  testMcpConnections: (teamId: string) =>
    api.post<AgentHealth['mcp_status']>(`/teams/${teamId}/agent/mcp-check`),

  advanceTask: (teamId: string, taskId: string, targetStage: string) =>
    api.post(`/teams/${teamId}/agent/tasks/${taskId}/advance`, { target_stage: targetStage }),

  getJiraSpyHistory: (teamId: string) =>
    api.get(`/teams/${teamId}/agent/jira-spy-history`),

  startInteractiveSession: (teamId: string): Promise<InteractiveSession> =>
    api.post(`/teams/${teamId}/agent/interactive`).then((r) => r.data),

  stopInteractiveSession: (
    teamId: string,
  ): Promise<{ duration_minutes: number; turn_count: number; total_cost_usd: number }> =>
    api.post(`/teams/${teamId}/agent/interactive/stop`).then((r) => r.data),

  getInteractiveConsoleUrl: (teamId: string): Promise<{ console_url: string }> =>
    api.get(`/teams/${teamId}/agent/interactive/console`).then((r) => r.data),

  listInteractiveSessions: (teamId: string): Promise<InteractiveSessionItem[]> =>
    api.get(`/teams/${teamId}/agent/interactive/sessions`).then((r) => r.data),

  decommissionInteractiveSession: (
    teamId: string,
    sessionId: string,
  ): Promise<{ duration_minutes: number; turn_count: number; total_cost_usd: number }> =>
    api
      .post(`/teams/${teamId}/agent/interactive/${sessionId}/stop`)
      .then((r) => r.data),

  getTaskLogToken: (taskId: string): Promise<{ log_token: string }> =>
    api.get(`/tasks/${taskId}/log-token`).then((r) => r.data),

  getSessionConsoleToken: (sessionId: string): Promise<{ console_token: string }> =>
    api.get(`/sessions/${sessionId}/console-token`).then((r) => r.data),
};

// =============================================================================
// SKILLS & PLUGINS API
// =============================================================================

export interface CatalogEntryRequestCreate {
  name: string;
  entry_type: 'skill' | 'plugin';
  marketplace_repo: string;
  marketplace_name: string;
  description?: string;
  source_url?: string;
}

export interface CatalogEntryRequest {
  id: string;
  team_id: string;
  requested_by_user_id?: string;
  name: string;
  entry_type: 'skill' | 'plugin';
  marketplace_repo: string;
  marketplace_name: string;
  description?: string;
  source_url?: string;
  status: 'pending' | 'approved' | 'rejected';
  review_note?: string;
  approved_catalog_entry_id?: string;
  created_at: string;
  reviewed_at?: string;
}

export interface CatalogEntryRequestAdmin extends CatalogEntryRequest {
  team_name?: string;
}

export const skillsPluginsApi = {
  getTeamCatalog: (teamId: string) =>
    api.get<TeamCatalogEntry[]>(`/teams/${teamId}/skills-plugins`),
  setInstallation: (teamId: string, entryId: string, enabled: boolean) =>
    api.put<{ message: string }>(`/teams/${teamId}/skills-plugins/${entryId}`, { enabled }),

  // Team request endpoints
  submitRequest: (teamId: string, data: CatalogEntryRequestCreate) =>
    api.post<CatalogEntryRequest>(`/teams/${teamId}/skills-plugins/requests`, data),
  getTeamRequests: (teamId: string) =>
    api.get<CatalogEntryRequest[]>(`/teams/${teamId}/skills-plugins/requests`),

  // Admin request endpoints
  getAdminRequests: (status?: string) =>
    api.get<CatalogEntryRequestAdmin[]>(`/admin/catalog/requests`, {
      params: status ? { status } : {},
    }),
  reviewRequest: (requestId: string, data: { action: 'approve' | 'reject'; review_note?: string }) =>
    api.patch<CatalogEntryRequest>(`/admin/catalog/requests/${requestId}`, data),
};

// =============================================================================
// WORKFLOW TYPES
// =============================================================================

export type WorkflowCompletionNotice = 'thread' | 'off' | 'dm_creator';

export interface Workflow {
  id: string;
  team_id: string;
  name: string;
  description: string | null;
  trigger_type: 'cron' | 'manual' | 'event';
  cron_expression: string | null;
  event_type: string | null;
  event_filter: Record<string, string> | null;
  goal_prompt: string;
  template_id: string | null;
  enabled_integrations: string[];
  selected_skills: string[];
  output_destinations: WorkflowOutputDestination[];
  origin_space_id: string | null;
  completion_notice: WorkflowCompletionNotice;
  created_by_user_id: string | null;
  creator_email: string | null;
  is_active: boolean;
  is_archived: boolean;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export type WorkflowOutputDestination =
  | { type: 'log' }
  | { type: 'gchat'; space_id: string }
  | { type: 'jira'; ticket_key?: string }
  | { type: 'chat_sender' }
  | { type: 'dm'; emails: string }
  | { type: 'email'; recipients: string; subject?: string };

export interface WorkflowCreate {
  name: string;
  description?: string;
  trigger_type: 'cron' | 'manual' | 'event';
  cron_expression?: string;
  event_type?: string;
  event_filter?: Record<string, string>;
  goal_prompt: string;
  template_id?: string;
  enabled_integrations: string[];
  selected_skills?: string[];
  output_destinations: WorkflowOutputDestination[];
  origin_space_id?: string;
  completion_notice: WorkflowCompletionNotice;
  is_active: boolean;
}

export interface WorkflowUpdate extends Partial<WorkflowCreate> {}

export interface WorkflowRunStep {
  tool: string;
  input_summary: string;
  output_summary: string;
  timestamp: string;
}

export interface WorkflowRun {
  id: string;
  workflow_id: string;
  team_id: string;
  triggered_by: 'cron' | 'manual' | 'event' | 'chat';
  trigger_context: Record<string, unknown> | null;
  status: 'pending' | 'running' | 'completed' | 'failed';
  goal_prompt: string;
  steps: WorkflowRunStep[];
  output_text: string | null;
  task_id: string | null;
  error_msg: string | null;
  started_at: string | null;
  completed_at: string | null;
}

// =============================================================================
// WORKFLOWS API
// =============================================================================

export const workflowsApi = {
  list: (teamId: string) =>
    api.get<Workflow[]>(`/teams/${teamId}/workflows`),
  create: (teamId: string, data: WorkflowCreate) =>
    api.post<Workflow>(`/teams/${teamId}/workflows`, data),
  get: (teamId: string, workflowId: string) =>
    api.get<Workflow>(`/teams/${teamId}/workflows/${workflowId}`),
  update: (teamId: string, workflowId: string, data: WorkflowUpdate) =>
    api.put<Workflow>(`/teams/${teamId}/workflows/${workflowId}`, data),
  delete: (teamId: string, workflowId: string) =>
    api.delete<void>(`/teams/${teamId}/workflows/${workflowId}`),
  archive: (teamId: string, workflowId: string) =>
    api.patch<Workflow>(`/teams/${teamId}/workflows/${workflowId}/archive`),
  triggerRun: (teamId: string, workflowId: string) =>
    api.post<{ message: string }>(`/teams/${teamId}/workflows/${workflowId}/run`),
  listRuns: (teamId: string, workflowId: string, limit = 50, offset = 0) =>
    api.get<WorkflowRun[]>(`/teams/${teamId}/workflows/${workflowId}/runs`, {
      params: { limit, offset },
    }),
  getRun: (teamId: string, workflowId: string, runId: string) =>
    api.get<WorkflowRun>(`/teams/${teamId}/workflows/${workflowId}/runs/${runId}`),
};

// =============================================================================
// ADMIN CATALOG API
// =============================================================================

export const adminCatalogApi = {
  list: () => api.get<CatalogEntry[]>('/admin/catalog'),
  create: (data: CatalogEntryCreate) => api.post<CatalogEntry>('/admin/catalog', data),
  update: (entryId: string, data: Partial<CatalogEntryCreate & { is_active: boolean }>) =>
    api.patch<CatalogEntry>(`/admin/catalog/${entryId}`, data),
};

// =============================================================================
// GLOBAL AGENT LESSONS API
// =============================================================================

export interface GlobalAgentLesson {
  id: string;
  title: string;
  body: string;
  category: string;
  approved: boolean;
  is_active: boolean;
  source_task_id: string | null;
  source_team_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface GlobalAgentLessonCreate {
  title: string;
  body: string;
  category?: string;
  approved?: boolean;
  is_active?: boolean;
}

export interface GlobalAgentLessonUpdate {
  title?: string;
  body?: string;
  category?: string;
  approved?: boolean;
  is_active?: boolean;
}

export const agentLessonsApi = {
  list: (params?: { approved?: boolean; is_active?: boolean }) =>
    api.get<GlobalAgentLesson[]>('/admin/agent-lessons', { params }),
  create: (data: GlobalAgentLessonCreate) =>
    api.post<GlobalAgentLesson>('/admin/agent-lessons', data),
  update: (lessonId: string, data: GlobalAgentLessonUpdate) =>
    api.patch<GlobalAgentLesson>(`/admin/agent-lessons/${lessonId}`, data),
  delete: (lessonId: string) =>
    api.delete(`/admin/agent-lessons/${lessonId}`),
};

// =============================================================================
// DM CONTACTS API
// =============================================================================

export interface DmContact {
  id: string;
  email: string;
  display_name: string | null;
  note: string | null;
  created_at: string;
}

export interface DmContactCreate {
  email: string;
  display_name?: string;
  note?: string;
}

export interface SendDmResponse {
  space_name: string;
  sent: boolean;
}

export const getDmContacts = (teamId: string): Promise<DmContact[]> =>
  api.get(`/teams/${teamId}/dm-contacts`).then((r) => r.data);

export const createDmContact = (teamId: string, data: DmContactCreate): Promise<DmContact> =>
  api.post(`/teams/${teamId}/dm-contacts`, data).then((r) => r.data);

export const deleteDmContact = (teamId: string, contactId: string): Promise<void> =>
  api.delete(`/teams/${teamId}/dm-contacts/${contactId}`).then((r) => r.data);

export const sendTestDm = (email: string, message: string): Promise<SendDmResponse> =>
  api.post('/bot/dm', { email, message }).then((r) => r.data);

export default api;
